// @ts-nocheck
/**
 * chat-send.js — Transport pipeline for chat messages.
 *
 * Handles:
 * - SSE streaming path
 * - sync fallback path
 * - timeout + abort behavior
 * - consistent error UX
 */
(function () {
  'use strict';
  var ORDER_NOTE_SPECIAL_PREFIX = 'AI Florist Special Instructions:';
  var DEFAULT_STREAM_REQUEST_TIMEOUT_MS = 120000;
  var GENERATION_STREAM_REQUEST_TIMEOUT_MS = 210000;

  window.AIFlorist = window.AIFlorist || {};
  var debug = window.AIFlorist.debug || { log: function () {}, warn: function () {}, error: function () {} };

  var workerUrl = '';
  var apiFetch = null;
  var stream = null;
  var render = null;
  var analytics = null;
  var setChatTitle = function () {};
  var onConversationId = function () {};
  var onBusyChange = function () {};
  var onSessionExpired = function () {};
  var onGenerationStart = function () {};
  var onProceedToOrder = function () {};
  var onOpenWeddingCreative = function () {};
  var onOpenWeddingDetails = function () {};
  var triggerMarketingPrompt = null;
  var getRequesterId = function () { return ''; };
  var generationPollToken = 0;
  var pusherConfig = null;
  var pusherInstance = null;
  var pendingArrangementCompletion = null;
  // Tracks the latest server-reported conversation state so the transport layer
  // can extend the request timeout for revision turns (post-generation states).
  // In layman's terms: once a bouquet is on the table being reviewed, every
  // follow-up message is treated as a long bake, not a quick chat reply.
  var currentConversationState = null;
  // Latched flag: true once the conversation has produced AT LEAST ONE
  // bouquet (state hit `reviewing` or an assistant message arrived with an
  // imageUrl). The orchestrator often dances back to `gathering_criteria`
  // mid-revision to ask clarifying questions, so we cannot rely on the live
  // state alone. Once flipped, every subsequent text turn arms the long
  // generation timeout — only "New Chat" / `session_expired` resets it.
  // Layman's terms: once flowers are in the vase, every follow-up message is
  // treated as "might trigger another bake" until the customer starts over.
  var hasReachedReviewing = false;

  function setConversationState(state) {
    if (state === null || state === undefined || state === '') {
      currentConversationState = null;
      hasReachedReviewing = false;
      return;
    }
    currentConversationState = String(state).trim() || null;
    if (isPostGenerationState()) {
      hasReachedReviewing = true;
    }
  }

  function noteAssistantMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object') return;
    if (metadata.imageUrl || metadata.arrangementName) {
      hasReachedReviewing = true;
    }
  }

  function isPostGenerationState() {
    return currentConversationState === 'reviewing'
      || currentConversationState === 'preparing_order'
      || currentConversationState === 'generating';
  }

  function init(deps) {
    workerUrl = deps.workerUrl;
    apiFetch = deps.apiFetch;
    stream = deps.stream || null;
    render = deps.render;
    analytics = deps.analytics || null;
    setChatTitle = deps.setChatTitle || setChatTitle;
    onConversationId = deps.onConversationId || onConversationId;
    onBusyChange = deps.onBusyChange || onBusyChange;
    onSessionExpired = deps.onSessionExpired || onSessionExpired;
    onGenerationStart = deps.onGenerationStart || onGenerationStart;
    onProceedToOrder = deps.onProceedToOrder || onProceedToOrder;
    onOpenWeddingCreative = deps.onOpenWeddingCreative || onOpenWeddingCreative;
    onOpenWeddingDetails = deps.onOpenWeddingDetails || onOpenWeddingDetails;
    triggerMarketingPrompt = deps.triggerMarketingPrompt || null;
    getRequesterId = deps.getRequesterId || getRequesterId;
  }

  async function send(payload, _isRetry, uiOptions) {
    generationPollToken++;
    onBusyChange(true);
    if (stream) {
      return sendViaStream(payload, _isRetry, uiOptions);
    }
    return sendViaSync(payload, _isRetry, uiOptions);
  }

  function retryAsNewConversation(payload) {
    debug.warn('[AI Florist] Session expired — auto-retrying as new conversation.');
    onSessionExpired();
    var fresh = Object.assign({}, payload);
    delete fresh.conversationId;
    delete fresh._expectExisting;
    send(fresh, true);
  }

  function hasGenerationIntent(payload, uiOptions) {
    if (uiOptions && uiOptions.expectGeneration === true) return true;
    if (uiOptions && uiOptions.expectGeneration === false) return false;
    // Bug 1b: revision turns sent after a bouquet has been generated almost
    // always trigger another generate_arrangement call. Use the longer
    // generation timeout so the SSE stream isn't aborted while Fleur thinks.
    // The latch covers the orchestrator's common "ask a clarifying question
    // first" dance which otherwise drops the live state back to
    // `gathering_criteria`.
    if (hasReachedReviewing || isPostGenerationState()) return true;
    if (!payload) return false;
    if (payload.image && payload.image.data) return true;
    if (payload.images && payload.images.length > 0) return true;
    if (payload.referenceImages && payload.referenceImages.length > 0) return true;
    if (payload.resolvedReferenceImages && payload.resolvedReferenceImages.length > 0) return true;
    if (payload.replyTo && payload.replyTo.hasImage) return true;
    return false;
  }

  function isBusyAssistantMessage(content) {
    return /still finishing your previous request/i.test(String(content || ''));
  }

  function rememberPendingArrangementCompletion(conversationId, startedAtMs) {
    if (!conversationId) return;
    pendingArrangementCompletion = {
      conversationId: conversationId,
      startedAtMs: Number(startedAtMs || Date.now()),
    };
  }

  function getPendingArrangementCompletion(conversationId) {
    if (!pendingArrangementCompletion) return null;
    if (conversationId && pendingArrangementCompletion.conversationId !== conversationId) return null;
    return pendingArrangementCompletion;
  }

  function clearPendingArrangementCompletion(conversationId) {
    if (!pendingArrangementCompletion) return;
    if (conversationId && pendingArrangementCompletion.conversationId !== conversationId) return;
    pendingArrangementCompletion = null;
  }

  async function sendViaStream(payload, _isRetry, uiOptions) {
    render.setInputLocked(true);
    var controller = new AbortController();
    var streamTimeoutMs = hasGenerationIntent(payload, uiOptions)
      ? GENERATION_STREAM_REQUEST_TIMEOUT_MS
      : DEFAULT_STREAM_REQUEST_TIMEOUT_MS;
    var timeoutId = setTimeout(function () { controller.abort(); }, streamTimeoutMs);
    var sessionExpiredFlag = false;
    var generationStarted = false;
    var queuePollingStarted = false;
    var queueStartedAt = Date.now();
    var activeConversationId = payload.conversationId || null;

    function handleGenerationStarted() {
      if (generationStarted) return;
      generationStarted = true;
      if (activeConversationId) {
        rememberPendingArrangementCompletion(activeConversationId, queueStartedAt);
      }
      render.removeTypingDots();
      render.showDesigningCard();
      if (analytics && analytics.trackGenerationStarted) {
        analytics.trackGenerationStarted(!!payload.conversationId);
      }
      onGenerationStart();
    }

    try {
      await stream.send(workerUrl, payload, {
        onAck: function (newId) {
          if (newId) {
            activeConversationId = newId;
            onConversationId(newId);
            if (hasGenerationIntent(payload, uiOptions) || generationStarted) {
              rememberPendingArrangementCompletion(newId, queueStartedAt);
            }
          }
        },
        onTyping: function () {
          render.showTypingDots();
        },
        onToolStart: function (tool) {
          if (tool === 'generate_arrangement') {
            handleGenerationStarted();
          }
        },
        onDesigning: function () {
          handleGenerationStarted();
        },
        onToolComplete: function () {},
        onMessage: function (content, metadata, cid, state) {
          if (cid) {
            activeConversationId = cid;
            onConversationId(cid);
          }
          if (state) setConversationState(state);
          noteAssistantMetadata(metadata);

          if (hasRichResultMetadata(metadata)) {
            clearPendingArrangementCompletion(cid || activeConversationId);
          }

          if (state === 'generating' && !hasRichResultMetadata(metadata)) {
            handleGenerationStarted();
            queuePollingStarted = true;
            return;
          }

          if (isBusyAssistantMessage(content)) {
            var pending = getPendingArrangementCompletion(activeConversationId);
            if (pending) {
              queuePollingStarted = true;
              queueStartedAt = pending.startedAtMs;
            }
          }

          render.removeIndicators();
          renderAssistantMessage(content, metadata, cid);
        },
        onError: function (msg) {
          render.removeIndicators();
          if (msg === 'session_expired' && !_isRetry) {
            sessionExpiredFlag = true;
            return;
          }
          render.systemError(msg || 'Something went wrong. Please try again.');
        },
        onDone: function () {
          clearTimeout(timeoutId);
          if (sessionExpiredFlag) {
            retryAsNewConversation(payload);
            return;
          }
          if (queuePollingStarted && activeConversationId) {
            waitForQueuedCompletion(activeConversationId, queueStartedAt, generationPollToken);
            return;
          }
          clearPendingArrangementCompletion(activeConversationId);
          onBusyChange(false);
          render.removeIndicators();
          render.setInputLocked(false);
        },
      }, controller.signal, { uiOptions: uiOptions || null, allowSyncFallback: true });
    } catch (err) {
      clearTimeout(timeoutId);
      var pending = getPendingArrangementCompletion(activeConversationId);
      var shouldRecover =
        err &&
        err.name === 'AbortError' &&
        activeConversationId &&
        (pending || hasGenerationIntent(payload, uiOptions) || generationStarted);
      if (shouldRecover) {
        var startedAtMs = pending && pending.startedAtMs
          ? pending.startedAtMs
          : queueStartedAt;
        rememberPendingArrangementCompletion(activeConversationId, startedAtMs);
        render.removeIndicators();
        render.showDesigningCard();
        waitForQueuedCompletion(activeConversationId, startedAtMs, generationPollToken);
        return;
      }
      handleSendError(err, payload.message);
    }
  }

  function hasRichResultMetadata(metadata) {
    return !!(metadata && (
      metadata.imageUrl ||
      (metadata.variations && metadata.variations.length > 0) ||
      metadata.priceBreakdown ||
      metadata.checkoutUrl ||
      (metadata.weddingConcepts && metadata.weddingConcepts.length > 0) ||
      (metadata.weddingHandoffCtas && metadata.weddingHandoffCtas.length > 0) ||
      metadata.showWeddingCreativeForm ||
      metadata.showWeddingDetailsForm
    ));
  }

  function renderAssistantMessage(content, metadata, cid) {
    render.message('assistant', content, metadata);
    maybeTriggerMarketingPrompt(metadata);
    if (analytics && analytics.trackPriceReconciled && metadata && metadata.priceReconciled) {
      analytics.trackPriceReconciled({
        original: metadata.priceReconciled.original,
        corrected: metadata.priceReconciled.corrected,
        delta: metadata.priceReconciled.delta,
        placeholder: metadata.priceReconciled.placeholder,
      });
    }
    if (
      analytics &&
      analytics.trackToolErrorSilentRetry &&
      metadata &&
      metadata.toolErrorSilentRetry
    ) {
      analytics.trackToolErrorSilentRetry({
        toolName: metadata.toolErrorSilentRetry.toolName,
        errorCode: metadata.toolErrorSilentRetry.errorCode,
        attempts: metadata.toolErrorSilentRetry.attempts,
        succeeded: metadata.toolErrorSilentRetry.succeeded,
      });
    }
    if (
      analytics &&
      analytics.trackStructuredCompactionAttempt &&
      metadata &&
      metadata.compactionTelemetry
    ) {
      var ct = metadata.compactionTelemetry;
      analytics.trackStructuredCompactionAttempt({
        attempted: ct.attempted,
        inCanary: ct.inCanary,
        result: ct.result,
        sourceMessages: ct.sourceMessages,
        missingTagCount: ct.missingTagCount,
        tooLong: ct.tooLong,
      });
      if (ct.breakerJustTripped && analytics.trackStructuredCompactionBreakerTripped) {
        analytics.trackStructuredCompactionBreakerTripped({
          consecutiveFailures: ct.consecutiveFailures,
          result: ct.result,
          sourceMessages: ct.sourceMessages,
        });
      }
    }
    if (
      analytics &&
      analytics.trackGenerationCompleted &&
      hasRichResultMetadata(metadata)
    ) {
      analytics.trackGenerationCompleted({
        hasImage: !!(metadata && metadata.imageUrl),
        hasWeddingConcepts: !!(metadata && metadata.weddingConcepts && metadata.weddingConcepts.length > 0),
        hasPriceBreakdown: !!(metadata && metadata.priceBreakdown),
        hasCheckoutUrl: !!(metadata && metadata.checkoutUrl),
      });
      if (metadata && metadata.imageUrl && analytics.trackImageGenerated) {
        analytics.trackImageGenerated({
          hasPriceBreakdown: !!metadata.priceBreakdown,
          hasCheckoutUrl: !!metadata.checkoutUrl,
          hasArrangementName: !!metadata.arrangementName,
        });
      }
    }
    render.clearFailedMessage();
    if (cid) onConversationId(cid);
    if (metadata && metadata.arrangementName) {
      setChatTitle(metadata.arrangementName);
    }
    if (metadata && metadata.showOrderForm) {
      onProceedToOrder(metadata.deliveryDetails || null);
    }
    if (metadata && metadata.showWeddingDetailsForm) {
      onOpenWeddingDetails(metadata.weddingProjectPrefill || null);
    }
  }

  function maybeTriggerMarketingPrompt(metadata) {
    if (!metadata || !metadata.showMarketingOptIn) return;
    if (typeof triggerMarketingPrompt !== 'function') return;
    triggerMarketingPrompt(function () {});
  }

  function setPusherConfig(config) {
    if (config && config.key && config.cluster) {
      pusherConfig = config;
    }
  }

  function getPusherInstance() {
    if (pusherInstance) return pusherInstance;
    if (!pusherConfig || typeof window.Pusher === 'undefined') return null;
    pusherInstance = new window.Pusher(pusherConfig.key, {
      cluster: pusherConfig.cluster,
      authEndpoint: workerUrl + '/pusher/auth',
      auth: {
        params: {
          requester_id: getRequesterId() || '',
        },
      },
    });
    return pusherInstance;
  }

  async function waitForQueuedCompletion(conversationId, startedAtMs, pollToken) {
    var maxWaitMs = 240000;
    var intervalMs = 2500;
    var startedPollMs = Date.now();
    var delivered = false;

    function handleDelivery(content, metadata) {
      if (delivered) return;
      delivered = true;
      clearPendingArrangementCompletion(conversationId);
      render.removeIndicators();
      renderAssistantMessage(content, metadata || null, conversationId);
      onBusyChange(false);
      render.setInputLocked(false);
      cleanupPusherSubscription(conversationId);
    }

    var pusher = getPusherInstance();
    if (pusher) {
      try {
        var channelName = 'private-conv-' + conversationId;
        var channel = pusher.subscribe(channelName);
        channel.bind('arrangement_ready', function (data) {
          handleDelivery(data.content, data.metadata);
        });
        channel.bind('arrangement_failed', function (data) {
          handleDelivery(data.content, null);
        });
      } catch (err) {
        debug.warn('[AI Florist] Pusher subscription failed, relying on poll:', err);
      }
    }

    while (Date.now() - startedPollMs < maxWaitMs) {
      if (delivered || pollToken !== generationPollToken) return;
      try {
        var res = await apiFetch(workerUrl + '/conversation/' + conversationId + '/messages', {
          method: 'GET',
          headers: buildWorkerHeaders(),
        });
        if (res.ok) {
          var data = await res.json();
          var history = (data && data.history) || [];
          var completed = findCompletedArrangementMessage(history, startedAtMs);
          if (completed) {
            handleDelivery(completed.content, completed.metadata);
            return;
          }
        }
      } catch (err) {
        debug.warn('[AI Florist] Queue completion poll failed:', err);
      }
      await sleep(intervalMs);
    }

    if (!delivered) {
      clearPendingArrangementCompletion(conversationId);
      render.removeIndicators();
      onBusyChange(false);
      render.setInputLocked(false);
      render.systemError(
        "I'm still finishing your arrangement. Please wait a little longer — your image will appear here automatically."
      );
    }
  }

  function cleanupPusherSubscription(conversationId) {
    if (!pusherInstance) return;
    try {
      pusherInstance.unsubscribe('private-conv-' + conversationId);
    } catch (e) { /* swallow */ }
  }

  function findCompletedArrangementMessage(history, startedAtMs) {
    var earliestAllowedTs = Number(startedAtMs || 0) - 2000;
    for (var i = history.length - 1; i >= 0; i--) {
      var msg = history[i];
      if (!msg || msg.role !== 'assistant') continue;
      if (earliestAllowedTs > 0) {
        var ts = Number(msg.timestamp || 0);
        if (ts > 0 && ts < earliestAllowedTs) continue;
      }
      if (hasRichResultMetadata(msg.metadata)) {
        return msg;
      }
    }
    return null;
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  async function sendViaSync(payload, _isRetry, uiOptions) {
    render.setLoading(true, uiOptions || null);
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, 180000);

    try {
      var res = await apiFetch(workerUrl + '/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      if (!res.ok) throw new Error('Request failed: ' + res.status);

      var data = await res.json();

      if (data.error === 'session_expired' && !_isRetry) {
        retryAsNewConversation(payload);
        return;
      }

      onConversationId(data.conversationId);
      if (data.state) setConversationState(data.state);
      noteAssistantMetadata(data.message && data.message.metadata);
      renderAssistantMessage(data.message.content, data.message.metadata || null, data.conversationId);
    } catch (err) {
      clearTimeout(timeoutId);
      handleSendError(err, payload.message);
    } finally {
      onBusyChange(false);
      render.setLoading(false, null);
    }
  }

  function handleSendError(err, text) {
    debug.error('[AI Florist] Chat error:', err);
    render.removeIndicators();
    render.setFailedMessage(text);

    if (err && err.name === 'AbortError') {
      render.systemError(
        'The request took too long. This might happen during image creation - please try again!'
      );
    } else {
      render.systemError('Something went wrong. Please try again.');
    }

    onBusyChange(false);
    render.setInputLocked(false);
  }

  /**
   * Deterministic product creation — no AI agent involved.
   * Calls a dedicated endpoint that directly creates the Shopify product
   * using arrangement data already stored in the Durable Object.
   * If this fails, prepare_checkout (Phase 2) creates it as a fallback.
   */
  async function sendBackground(conversationId, selectionRef) {
    var data = await sendPrepareProduct(conversationId, selectionRef, null);
    if (data) {
      debug.log('[AI Florist] Background product creation:', data.status, data.message);
    }
  }

  async function sendPrepareProduct(conversationId, selectionRef, options) {
    if (!conversationId) return null;
    var requesterId = getRequesterId ? getRequesterId() : '';
    try {
      var res = await apiFetch(workerUrl + '/conversation/' + conversationId + '/prepare-product', {
        method: 'POST',
        headers: buildWorkerHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          selectionRef: selectionRef || undefined,
          forceRecreate: !!(options && options.forceRecreate),
          requesterId: requesterId || undefined,
        }),
      });
      if (!res.ok) {
        debug.warn('[AI Florist] prepare-product HTTP ' + res.status);
        try {
          var errBody = await res.json();
          return { status: 'error', message: (errBody && errBody.error) || ('Server error (' + res.status + ')') };
        } catch (_e) {
          return { status: 'error', message: 'Server error (' + res.status + ')' };
        }
      }
      return await res.json();
    } catch (err) {
      debug.warn('[AI Florist] prepare-product failed:', err);
      return { status: 'error', message: 'Network error — please check your connection and try again.' };
    }
  }

  async function addPreparedVariantToCart(params) {
    var variantId = params && params.variantId ? Number(params.variantId) : 0;
    if (!variantId) {
      return {
        success: false,
        message: 'No variant available to add to cart.',
      };
    }

    var properties = {
      _afc_source: 'ai-florist',
      _afc_channel: 'storefront',
    };

    if (params && params.cardMessage) {
      properties['Card Message'] = String(params.cardMessage).trim();
    }
    if (params && params.conversationId) {
      properties._afc_conversation_id = String(params.conversationId);
    }
    if (params && params.selectionRef) {
      if (params.selectionRef.imageUrl) {
        properties._afc_selection_image = String(params.selectionRef.imageUrl);
      }
      if (params.selectionRef.arrangementName) {
        properties._afc_arrangement_name = String(params.selectionRef.arrangementName);
      }
    }

    try {
      var backoffMs = [250, 450, 800, 1300, 2100, 3200];
      var lastStatus = 0;
      var lastBody = '';
      for (var attempt = 0; attempt <= backoffMs.length; attempt++) {
        if (params && typeof params.onProgress === 'function') {
          params.onProgress({ stage: attempt === 0 ? 'finalizing' : 'retrying', attempt: attempt + 1 });
        }

        var addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: properties,
              },
            ],
          }),
        });

        if (!addRes.ok) {
          // Fallback to legacy payload shape for stricter themes/apps.
          addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              properties: properties,
            }),
          });
        }

        if (addRes.ok) {
          break;
        }

        lastStatus = addRes.status;
        lastBody = await addRes.text().catch(function () { return ''; });
        var retryable = isRetryableAddToCartFailure(lastStatus, lastBody);
        if (!retryable || attempt >= backoffMs.length) {
          return {
            success: false,
            transient: retryable,
            message: retryable
              ? 'This arrangement is still syncing with Shopify. Please try again in a moment.'
              : 'cart/add.js failed: HTTP ' + lastStatus + ' ' + lastBody.slice(0, 220),
          };
        }
        await sleep(backoffMs[attempt]);
      }

      var cartState = await fetch('/cart.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });

      if (!cartState || !cartState.item_count) {
        return {
          success: false,
          message: 'Cart remains empty after add.',
        };
      }

      // Notify Horizon cart systems so drawer + cart icon refresh in-place.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartState,
            sourceId: 'aiflorist-chat',
            data: {
              source: 'product-form-component',
              itemCount: Number(cartState.item_count) || 0,
            },
          },
        })
      );

      trackAddToOrderEvent({
        conversationId: params && params.conversationId ? String(params.conversationId) : '',
        customer: params && params.customer ? params.customer : null,
        selectionRef: params && params.selectionRef ? params.selectionRef : null,
        variantId: variantId,
        cardMessage: params && params.cardMessage ? params.cardMessage : '',
      }).catch(function (err) {
        debug.warn('[AI Florist] add-to-order event tracking failed:', err);
      });

      return {
        success: true,
        itemCount: Number(cartState.item_count) || 0,
      };
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : String(err),
      };
    }
  }

  function isRetryableAddToCartFailure(status, responseBody) {
    if (status !== 422) return false;
    var body = String(responseBody || '').toLowerCase();
    return (
      body.indexOf('cart error') >= 0 ||
      body.indexOf('sold out') >= 0 ||
      body.indexOf('not available') >= 0 ||
      body.indexOf('unavailable') >= 0 ||
      body.indexOf('out of stock') >= 0 ||
      body.indexOf('inventory') >= 0 ||
      !body
    );
  }

  async function trackAddToOrderEvent(args) {
    if (!workerUrl) return;
    var payload = {
      conversationId: args && args.conversationId ? args.conversationId : undefined,
      timestamp: new Date().toISOString(),
      customer: args && args.customer ? args.customer : undefined,
      arrangementName:
        args && args.selectionRef && args.selectionRef.arrangementName
          ? String(args.selectionRef.arrangementName)
          : undefined,
      imageUrl:
        args && args.selectionRef && args.selectionRef.imageUrl
          ? String(args.selectionRef.imageUrl)
          : undefined,
      variantId: args && args.variantId ? Number(args.variantId) : undefined,
      cardMessage:
        args && args.cardMessage
          ? String(args.cardMessage)
          : undefined,
    };
    var request = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    };
    if (apiFetch) {
      await apiFetch(workerUrl + '/events/add-to-order', request);
    } else {
      await fetch(workerUrl + '/events/add-to-order', request);
    }
  }

  /**
   * Deterministic checkout — saves delivery details, creates cart,
   * returns checkout URL. Called before sending the composed message
   * to Fleur so the DO has all the data by the time Fleur processes.
   */
  async function sendCheckout(conversationId, deliveryDetails, attribution) {
    if (!conversationId) return null;

    try {
      var payload = Object.assign({}, deliveryDetails || {});
      if (attribution) {
        payload.attribution = attribution;
      }
      var res = await apiFetch(workerUrl + '/conversation/' + conversationId + '/checkout', {
        method: 'POST',
        headers: buildWorkerHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        var data = await res.json();
        debug.log('[AI Florist] Deterministic checkout:', data.status, data.message);
        return data;
      }
      return null;
    } catch (err) {
      debug.warn('[AI Florist] Deterministic checkout failed — Fleur will handle it:', err);
      return null;
    }
  }

  async function handoffToCartDrawer(checkoutResult, deliveryDetails, conversationId, attribution) {
    try {
      var variantId = checkoutResult && checkoutResult.variantId ? Number(checkoutResult.variantId) : 0;
      if (!variantId) return;

      var properties = {};
      if (deliveryDetails && deliveryDetails.cardMessage) {
        properties['Card Message'] = String(deliveryDetails.cardMessage);
      }

      var addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: properties,
            },
          ],
        }),
      });
      if (!addRes.ok) {
        // Fallback to legacy payload shape for stricter themes/apps.
        addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: properties,
          }),
        });
      }
      if (!addRes.ok) {
        var addBody = await addRes.text().catch(function () { return ''; });
        throw new Error('cart/add.js failed: HTTP ' + addRes.status + ' ' + addBody.slice(0, 220));
      }

      var attrs = {
        conversation_id: conversationId || '',
        source: 'ai-florist',
      };
      var existingOrderNote = '';
      if (deliveryDetails) {
        attrs['Delivery Method'] =
          deliveryDetails.fulfillmentType === 'collection' ? 'Self-collection' : 'Local delivery';
        var deliveryDateDisplay = formatDateDdMmYyyy(deliveryDetails.deliveryDate);
        if (deliveryDateDisplay) attrs.delivery_date = deliveryDateDisplay;
        var deliveryDateIso = toIsoDateYyyyMmDd(deliveryDetails.deliveryDate);
        if (deliveryDateIso) attrs.delivery_date_iso = deliveryDateIso;
        if (deliveryDetails.deliveryTimeslot) attrs.delivery_timeslot = deliveryDetails.deliveryTimeslot;
        var legacyDeliveryWindow = formatLegacyDeliveryWindow(
          attrs.delivery_date || deliveryDetails.deliveryDate,
          deliveryDetails.deliveryTimeslot
        );
        if (legacyDeliveryWindow) {
          attrs['Local Delivery Date and Time'] = legacyDeliveryWindow;
        }
        if (deliveryDetails.senderPhone) attrs.sender_phone = deliveryDetails.senderPhone;
        if (deliveryDetails.senderEmail) attrs.sender_email = deliveryDetails.senderEmail;
        if (deliveryDetails.specialInstructions) {
          existingOrderNote = await fetch('/cart.js', {
            method: 'GET',
            headers: { Accept: 'application/json' },
          })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (cart) { return cart && cart.note ? String(cart.note) : ''; })
            .catch(function () { return ''; });
        }
      }
      var firstTouch = attribution && attribution.firstTouchAttribution
        ? attribution.firstTouchAttribution
        : attribution;
      var lastTouch = attribution && attribution.lastTouchAttribution
        ? attribution.lastTouchAttribution
        : attribution;
      appendAttributionToAttributes(attrs, attribution, '');
      appendAttributionToAttributes(attrs, firstTouch, 'ft_');
      appendAttributionToAttributes(attrs, lastTouch, 'lt_');

      var updatePayload = { attributes: attrs };
      if (deliveryDetails && typeof deliveryDetails.specialInstructions !== 'undefined') {
        updatePayload.note = composeOrderNote(
          existingOrderNote,
          deliveryDetails.specialInstructions
        );
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(updatePayload),
      });

      var cartState = await fetch('/cart.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
      if (!cartState || !cartState.item_count) {
        throw new Error('Cart remains empty after add');
      }

      // Notify native Horizon cart systems so drawer + cart icon refresh in-place.
      // We intentionally omit section HTML payload to force section re-render.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartState,
            sourceId: 'aiflorist-chat',
            data: {
              source: 'product-form-component',
              itemCount: Number(cartState.item_count) || 0,
            },
          },
        })
      );
      await new Promise(function (resolve) { setTimeout(resolve, 120); });

      var drawerTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (drawerTrigger && typeof drawerTrigger.click === 'function') {
        drawerTrigger.click();
      } else {
        window.location.href = '/checkout';
      }
    } catch (err) {
      debug.warn('[AI Florist] Cart drawer handoff failed:', err);
      if (checkoutResult && checkoutResult.checkoutUrl) {
        window.location.href = checkoutResult.checkoutUrl;
      }
    }
  }

  /**
   * Deterministic revise intent — clears stale checkout/product context
   * in the DO before UI/AI revision actions continue.
   */
  async function sendRevise(conversationId, options) {
    if (!conversationId) return null;

    try {
      var res = await apiFetch(workerUrl + '/conversation/' + conversationId + '/revise', {
        method: 'POST',
        headers: buildWorkerHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(options || {}),
      });

      if (res.ok) {
        return await res.json();
      }
      return null;
    } catch (err) {
      debug.warn('[AI Florist] Deterministic revise failed:', err);
      return null;
    }
  }

  function buildWorkerHeaders(headers) {
    var next = Object.assign({}, headers || {});
    var requesterId = getRequesterId ? getRequesterId() : '';
    if (requesterId) {
      next['X-AIFLORIST-Requester-Id'] = requesterId;
    }
    return next;
  }

  function appendAttributionToAttributes(target, attribution, prefix) {
    if (!target || !attribution) return;

    function push(key, value) {
      if (!value) return;
      target[prefix + key] = String(value).trim();
    }

    push('utm_source', attribution.utmSource);
    push('utm_medium', attribution.utmMedium);
    push('utm_campaign', attribution.utmCampaign);
    push('utm_term', attribution.utmTerm);
    push('utm_content', attribution.utmContent);
    push('gclid', attribution.gclid);
    push('gbraid', attribution.gbraid);
    push('wbraid', attribution.wbraid);
    push('fbclid', attribution.fbclid);
    push('msclkid', attribution.msclkid);
    push('ttclid', attribution.ttclid);
    push('fbc', attribution.fbc);
    push('fbp', attribution.fbp);
    push('ga', attribution.ga);
    push('gl', attribution.gl);
    push('landing_page', attribution.landingPage);
    push('referrer', attribution.referrer);
  }

  function formatLegacyDeliveryWindow(rawDate, rawTimeslot) {
    var datePart = formatDateDdMmYyyy(rawDate);
    var timeslotPart = String(rawTimeslot || '').trim();
    if (timeslotPart) {
      timeslotPart = timeslotPart
        .replace(/\u2013/g, '-')
        .replace(/\u2014/g, '-')
        .replace(/\s*-\s*/g, ' to ');
    }
    if (datePart && timeslotPart) return datePart + ' ' + timeslotPart;
    if (datePart) return datePart;
    if (timeslotPart) return timeslotPart;
    return '';
  }

  function formatDateDdMmYyyy(rawDate) {
    var input = String(rawDate || '').trim();
    if (!input) return '';
    var iso = input.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
    if (iso) return iso[3] + '/' + iso[2] + '/' + iso[1];
    var dmy = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dmy) return input;
    return input;
  }

  function toIsoDateYyyyMmDd(rawDate) {
    var input = String(rawDate || '').trim();
    if (!input) return '';
    var iso = input.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
    if (iso) return iso[1] + '-' + iso[2] + '-' + iso[3];
    var dmy = input.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dmy) return dmy[3] + '-' + dmy[2] + '-' + dmy[1];
    return '';
  }

  function composeOrderNote(existingNote, specialInstructions) {
    var base = stripSpecialInstructionLines(existingNote);
    var special = String(specialInstructions || '').trim();
    if (!special) return base;
    var specialLine = ORDER_NOTE_SPECIAL_PREFIX + ' ' + special;
    if (!base) return specialLine;
    return base + '\n' + specialLine;
  }

  function stripSpecialInstructionLines(note) {
    var raw = String(note || '');
    if (!raw) return '';
    var lines = raw.split(/\r?\n/);
    var kept = [];
    for (var i = 0; i < lines.length; i++) {
      if (isSpecialInstructionLine(lines[i])) continue;
      kept.push(lines[i]);
    }
    return kept.join('\n').trim();
  }

  function isSpecialInstructionLine(line) {
    return /^AI Florist Special Instructions:/i.test(String(line || '').trim());
  }

  window.AIFlorist.send = {
    init: init,
    send: send,
    sendBackground: sendBackground,
    sendPrepareProduct: sendPrepareProduct,
    addPreparedVariantToCart: addPreparedVariantToCart,
    sendCheckout: sendCheckout,
    sendRevise: sendRevise,
    handoffToCartDrawer: handoffToCartDrawer,
    setPusherConfig: setPusherConfig,
    setConversationState: setConversationState,
    noteAssistantMetadata: noteAssistantMetadata,
  };
})();
